/**
 * HomeScreen — ruta `/` (reviewer+).
 *
 * Pantalla de inicio del panel Vera. Muestra:
 *   1) Carga masiva por carpeta (operator): arrastrar una carpeta CREA una
 *      ronda nueva (el nombre de la carpeta es su etiqueta) y carga los
 *      expedientes. Única vía de ingesta desde el inicio — las rutas viejas
 *      (alta individual, carga por tarjetas) ya no se ofrecen aquí.
 *   2) Tabla compacta de rondas recientes (máx. 5), siempre fresca. La ronda
 *      "default" se lista como cualquier otra (la sección "Bandeja de
 *      entrada" se retiró el 2026-07-04 por decisión del dueño).
 *
 * DEMO mode: usa datos sintéticos y omite llamadas reales.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import type { ChipProps } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { api, isApiError, useApiQuery } from "../api/useApi";
import { IS_DEMO } from "../api/client";
import type { RoundListResponse, RoundListItem } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { roleAtLeast } from "../auth/roles";
import { Loading } from "../components/Loading";
import { ProblemAlert } from "../components/ProblemAlert";
import { DemoBadge } from "../components/DemoBadge";
import { BulkFolderUploader } from "./operations/BulkFolderUploader";
import { demoProcess } from "./operations/demo";

// ── Status chip (reutiliza la misma paleta que RoundsListScreen) ──────────────
type RoundStatus = RoundListItem["status"];

const STATUS_META: Record<
  RoundStatus,
  { label: string; color: ChipProps["color"] }
> = {
  queued: { label: "En cola", color: "info" },
  running: { label: "En proceso", color: "secondary" },
  completed: { label: "Completada", color: "success" },
  failed: { label: "Con errores", color: "error" },
  closed: { label: "Cerrada", color: "default" },
  empty: { label: "Sin documentos", color: "warning" },
};

function StatusChip({ status }: { status: RoundStatus }) {
  const meta = STATUS_META[status] ?? {
    label: status,
    color: "default" as const,
  };
  return (
    <Chip
      size="small"
      label={meta.label}
      color={meta.color}
      variant="outlined"
    />
  );
}

// ── DEMO data ─────────────────────────────────────────────────────────────────
const DEMO_ROUNDS: RoundListResponse = {
  rounds: [
    {
      round_id: "default",
      display_name: "Bandeja de entrada",
      status: "empty",
      total: 0,
      generated_at: null,
    },
    {
      round_id: "ronda-2026-06",
      display_name: "Junio 2026",
      status: "completed",
      total: 1280,
      generated_at: "2026-06-08T18:42:00Z",
    },
    {
      round_id: "ronda-2026-05",
      display_name: null,
      status: "running",
      total: 540,
      generated_at: "2026-06-09T09:15:00Z",
    },
    {
      round_id: "ronda-2026-04",
      display_name: null,
      status: "failed",
      total: 96,
      generated_at: "2026-05-30T11:02:00Z",
    },
  ],
};

const DATE_FMT = new Intl.DateTimeFormat("es-MX", {
  dateStyle: "short",
  timeStyle: "short",
});
function fmt(v: string | null): string {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : DATE_FMT.format(d);
}

async function loadRounds(): Promise<RoundListResponse> {
  if (IS_DEMO) return DEMO_ROUNDS;
  const res = await api.GET("/rounds", {});
  return res.data!;
}

export default function HomeScreen() {
  const navigate = useNavigate();
  const { role } = useAuth();
  const isOperator = roleAtLeast(role, "operator");

  const { data, error, loading, reload } = useApiQuery<RoundListResponse>(
    loadRounds,
    [],
  );

  // "Procesar ronda" desde el inicio: la ronda recién creada por la carga.
  const [processTarget, setProcessTarget] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [processError, setProcessError] = useState<Error | null>(null);
  const [conflict, setConflict] = useState(false);

  /** Crea la ronda al confirmar la carga; el nombre de la carpeta la etiqueta. */
  async function createRoundForUpload(suggestedName: string): Promise<string> {
    if (IS_DEMO) return `ronda-demo-${Date.now()}`;
    const res = await api.POST("/rounds", {
      body: { display_name: suggestedName },
    });
    return res.data!.round_id;
  }

  async function handleProcess() {
    const target = processTarget;
    setProcessTarget(null);
    if (!target) return;
    setProcessing(true);
    setProcessError(null);
    setConflict(false);
    try {
      if (IS_DEMO) {
        demoProcess(target);
      } else {
        await api.POST("/rounds/{round_id}/process", {
          params: { path: { round_id: target } },
        });
      }
      // El batch corre fuera de banda: directo al monitor con progreso en vivo.
      navigate(`/rounds/${target}/monitor`);
    } catch (err: unknown) {
      if (isApiError(err) && err.status === 409) {
        setConflict(true);
      } else if (err instanceof Error) {
        setProcessError(err);
      } else {
        setProcessError(new Error(String(err)));
      }
    } finally {
      setProcessing(false);
    }
  }

  // Rondas recientes (máx. 5) — la default se lista como cualquier otra.
  const recentRounds = data?.rounds.slice(0, 5) ?? [];

  return (
    <Stack spacing={3}>
      {/* Encabezado */}
      <Box>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <Typography variant="h1">Inicio</Typography>
          <DemoBadge />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          Panel de validación de expedientes — Examen Único.
        </Typography>
      </Box>

      {/* Ingesta (operator): la carpeta ES la ronda — única vía de carga. */}
      {isOperator && (
        <Box>
          <Typography variant="h3" sx={{ mb: 1.5 }}>
            Nueva ronda
          </Typography>
          <BulkFolderUploader
            createRound={createRoundForUpload}
            onBulkUploaded={() => reload()}
            onRequestProcess={(rid) => setProcessTarget(rid)}
          />
          {conflict && (
            <Alert severity="warning" sx={{ mt: 2 }} onClose={() => setConflict(false)}>
              <AlertTitle>Ya hay un procesamiento en curso</AlertTitle>
              Esa ronda ya tiene una corrida en marcha; revisa su monitor.
            </Alert>
          )}
          {processError && (
            <Box sx={{ mt: 2 }}>
              <ProblemAlert error={processError} />
            </Box>
          )}
          <Dialog open={processTarget !== null} onClose={() => setProcessTarget(null)}>
            <DialogTitle>Procesar la ronda {processTarget}</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Esto arranca el procesamiento por lotes sobre los expedientes
                cargados. Corre en segundo plano y no puede deshacerse desde
                aquí. ¿Deseas continuar?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setProcessTarget(null)}>Cancelar</Button>
              <Button variant="contained" onClick={handleProcess} autoFocus disabled={processing}>
                Sí, procesar
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}

      {/* Estado de carga */}
      {loading && <Loading label="Cargando resumen…" />}
      {error && <ProblemAlert error={error} onRetry={reload} />}

      {data && (
        <Stack spacing={3}>
          {/* Rondas recientes */}
          {recentRounds.length > 0 && (
            <Box>
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  mb: 1.5,
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="h3">Rondas recientes</Typography>
                <Button size="small" onClick={() => navigate("/rounds")}>
                  Ver todas
                </Button>
              </Stack>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small" aria-label="Rondas recientes">
                  <TableHead>
                    <TableRow>
                      <TableCell>Ronda</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell align="right">Expedientes</TableCell>
                      <TableCell>Actualizada</TableCell>
                      <TableCell padding="checkbox" aria-hidden />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentRounds.map((r) => (
                      <TableRow
                        key={r.round_id}
                        hover
                        onClick={() =>
                          navigate(`/rounds/${encodeURIComponent(r.round_id)}`)
                        }
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell>
                          <Box>
                            {r.display_name && (
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600 }}
                              >
                                {r.display_name}
                              </Typography>
                            )}
                            <Typography
                              variant={r.display_name ? "caption" : "body2"}
                              color={
                                r.display_name
                                  ? "text.secondary"
                                  : "text.primary"
                              }
                              sx={{ fontFamily: "monospace" }}
                            >
                              {r.round_id}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <StatusChip status={r.status} />
                        </TableCell>
                        <TableCell align="right">
                          {r.total != null
                            ? r.total.toLocaleString("es-MX")
                            : "—"}
                        </TableCell>
                        <TableCell>{fmt(r.generated_at)}</TableCell>
                        <TableCell padding="checkbox">
                          <ChevronRightIcon color="action" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {recentRounds.length === 0 && (
            <Box>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Aún no hay rondas. Arrastra una carpeta de expedientes arriba
                para crear la primera.
              </Typography>
            </Box>
          )}
        </Stack>
      )}
    </Stack>
  );
}
